import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Link as MuiLink,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Brush,
  DarkMode,
  LightMode,
  Palette,
  Settings as SettingsIcon,
  Storefront,
} from '@mui/icons-material';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getTenant, updateTenant } from '@/api/tenants';
import { listProductSubscriptions, isUsable } from '@/api/products';
import {
  getPortalBrandingConfig,
  setPortalBrandingConfig,
  type PortalBrandingConfig,
} from '@/api/portalBranding';
import { toApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { useThemeMode } from '@/ThemeModeContext';
import { SUBSCRIPTION_STATUS_LABEL } from '@/lib/labels';
import { commercialProductLabelFromTechnical } from '@/lib/commercialProducts';
import { safeHexColor, safeHttpsUrl, safeMailtoHref } from '@/lib/safeExternalUrl';

export const Route = createFileRoute('/_authenticated/settings')({
  component: SettingsPage,
});

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  Active: 'success',
  Trial: 'warning',
  PastDue: 'error',
  Suspended: 'error',
  Cancelled: 'default',
};

function SettingsPage(): JSX.Element {
  const { tenantId } = useAuth();
  const [toast, setToast] = useState<string | null>(null);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <SettingsIcon color="primary" />
        <Typography variant="h4">Configuración</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Datos de la cuenta, branding del portal de clientes y preferencias.
      </Typography>

      <Stack spacing={3}>
        <AccountSection tenantId={tenantId} onSaved={() => setToast('Datos de la cuenta guardados.')} />
        <PortalBrandingSection onSaved={() => setToast('Branding del portal guardado.')} />
        <PreferencesSection />
      </Stack>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

// ── Cuenta ──────────────────────────────────────────────────────────────────
function AccountSection({
  tenantId,
  onSaved,
}: {
  tenantId: string | null;
  onSaved: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [dirty, setDirty] = useState(false);

  const tenantQuery = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => getTenant(tenantId as string),
    enabled: Boolean(tenantId),
  });

  const productSubscriptionsQuery = useQuery({
    queryKey: ['product-subscriptions', tenantId],
    queryFn: listProductSubscriptions,
    enabled: Boolean(tenantId),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (tenantQuery.data && !dirty) {
      setName(tenantQuery.data.name);
      setEmail(tenantQuery.data.email);
    }
  }, [tenantQuery.data, dirty]);

  const mutation = useMutation({
    mutationFn: () => updateTenant(tenantId as string, { name: name.trim(), email: email.trim() }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['tenant', tenantId], updated);
      setDirty(false);
      onSaved();
    },
  });

  const t = tenantQuery.data;
  const productSubscription = (productSubscriptionsQuery.data ?? [])
    .filter(isUsable)
    .sort((a, b) => {
      const order = { Empresa: 0, Estudio: 1, Api: 2, Admin: 3 } as const;
      return (order[a.product] ?? 99) - (order[b.product] ?? 99);
    })[0] ?? null;
  const productName = productSubscription
    ? productSubscription.commercialProductName
      ?? commercialProductLabelFromTechnical(
        productSubscription.product,
        productSubscription.tier,
        productSubscription.commercialProduct,
      )
    : 'Producto por clasificar';
  const productAndTier = productSubscription
    ? `${productName} · ${productSubscription.tierName}`
    : productName;
  const subscriptionStatus = productSubscription?.status ?? t?.subscriptionStatus;
  const maxCuitProfiles = productSubscription?.limits?.['max-cuit-profiles'] ?? t?.maxCuitProfiles;

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Storefront fontSize="small" color="action" />
        <Typography variant="h6">Cuenta</Typography>
      </Stack>

      {tenantQuery.isLoading ? (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : tenantQuery.isError ? (
        <Alert severity="error">{toApiError(tenantQuery.error).message}</Alert>
      ) : (
        <Stack
          component="form"
          spacing={2}
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && email.trim()) {
              mutation.mutate();
            }
          }}
        >
          {mutation.isError ? (
            <Alert severity="error">{toApiError(mutation.error).message}</Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Razón social"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setDirty(true);
                }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email de facturación"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setDirty(true);
                }}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>

          {t ? (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Producto actual
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {productSubscriptionsQuery.isLoading ? 'Cargando…' : productAndTier}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Suscripción
                  </Typography>
                  <Chip
                    label={
                      subscriptionStatus
                        ? SUBSCRIPTION_STATUS_LABEL[
                          subscriptionStatus as keyof typeof SUBSCRIPTION_STATUS_LABEL
                        ] ?? subscriptionStatus
                        : '—'
                    }
                    color={subscriptionStatus ? STATUS_COLOR[subscriptionStatus] ?? 'default' : 'default'}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    CUITs máximos
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {maxCuitProfiles ?? '—'}
                  </Typography>
                </Box>
                <Box sx={{ ml: 'auto' }}>
                  <Button component={Link} to="/billing" size="small" variant="text">
                    Gestionar producto y pagos
                  </Button>
                </Box>
              </Stack>
            </Box>
          ) : null}

          <Box>
            <Button
              type="submit"
              variant="contained"
              disabled={!dirty || !name.trim() || !email.trim() || mutation.isPending}
              startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Guardar cambios
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  );
}

// ── Branding del portal ───────────────────────────────────────────────────────
const EMPTY_BRANDING: PortalBrandingConfig = {
  displayName: '',
  logoUrl: null,
  primaryColor: null,
  supportEmail: null,
};

function PortalBrandingSection({ onSaved }: { onSaved: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<PortalBrandingConfig>(EMPTY_BRANDING);
  const [dirty, setDirty] = useState(false);

  const brandingQuery = useQuery({
    queryKey: ['portal-branding-config'],
    queryFn: getPortalBrandingConfig,
  });

  useEffect(() => {
    if (brandingQuery.data && !dirty) {
      setForm(brandingQuery.data);
    }
  }, [brandingQuery.data, dirty]);

  const mutation = useMutation({
    mutationFn: () =>
      setPortalBrandingConfig({
        displayName: form.displayName.trim(),
        logoUrl: form.logoUrl?.trim() || null,
        primaryColor: form.primaryColor?.trim() || null,
        supportEmail: form.supportEmail?.trim() || null,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['portal-branding-config'], saved);
      setDirty(false);
      onSaved();
    },
  });

  const set = <K extends keyof PortalBrandingConfig>(
    key: K,
    value: PortalBrandingConfig[K],
  ): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  // 402 = el plan no incluye co-branding → hint de upgrade en vez de error crudo.
  const planLocked = toApiError(mutation.error).status === 402;
  const displayName = form.displayName.trim();
  const logoInput = form.logoUrl?.trim() ?? '';
  const colorInput = form.primaryColor?.trim() ?? '';
  const supportEmailInput = form.supportEmail?.trim() ?? '';
  const logoError = logoInput !== '' && !safeHttpsUrl(logoInput);
  const colorError = colorInput !== '' && !safeHexColor(colorInput);
  const supportEmailError = supportEmailInput !== '' && !safeMailtoHref(supportEmailInput);
  const canSave = dirty && displayName !== '' && !logoError && !colorError && !supportEmailError && !mutation.isPending;
  const color = safeHexColor(colorInput) ?? '#1976d2';

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Brush fontSize="small" color="action" />
        <Typography variant="h6">Branding del portal de clientes</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Personalizá lo que ven tus clientes en el portal de pagos (login y comprobantes).
      </Typography>

      {brandingQuery.isLoading ? (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : (
        <Stack
          component="form"
          spacing={2}
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) {
              mutation.mutate();
            }
          }}
        >
          {planLocked ? (
            <Alert severity="info" icon={<Palette />}>
              El co-branding del portal está disponible en planes superiores.{' '}
              <MuiLink component={Link} to="/billing">
                Mejorá tu plan
              </MuiLink>{' '}
              para personalizar logo y colores.
            </Alert>
          ) : mutation.isError ? (
            <Alert severity="error">{toApiError(mutation.error).message}</Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nombre a mostrar"
                value={form.displayName}
                onChange={(e) => set('displayName', e.target.value)}
                fullWidth
                size="small"
                helperText="Nombre del estudio que ve el cliente."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email de soporte"
                type="email"
                value={form.supportEmail ?? ''}
                onChange={(e) => set('supportEmail', e.target.value)}
                fullWidth
                size="small"
                error={supportEmailError}
                helperText={supportEmailError ? 'Ingresá un email simple, sin asunto ni copias.' : 'Opcional. Se muestra como contacto del portal.'}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="URL del logo"
                value={form.logoUrl ?? ''}
                onChange={(e) => set('logoUrl', e.target.value)}
                fullWidth
                size="small"
                placeholder="https://…/logo.png"
                error={logoError}
                helperText={logoError ? 'La URL del logo debe ser https://.' : 'Opcional. Usá una URL pública HTTPS.'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Color primario"
                value={form.primaryColor ?? ''}
                onChange={(e) => set('primaryColor', e.target.value)}
                fullWidth
                size="small"
                placeholder="#1976d2"
                error={colorError}
                helperText={colorError ? 'Usá un color hexadecimal como #1976D2.' : 'Opcional. Formato #RGB o #RRGGBB.'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '4px',
                          bgcolor: color,
                          border: '1px solid rgba(0,0,0,0.2)',
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Box>
            <Button
              type="submit"
              variant="contained"
              disabled={!canSave}
              startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Guardar branding
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  );
}

// ── Preferencias ──────────────────────────────────────────────────────────────
function PreferencesSection(): JSX.Element {
  const { mode, toggle } = useThemeMode();
  const isDark = mode === 'dark';

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Palette fontSize="small" color="action" />
        <Typography variant="h6">Preferencias</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Tema de la interfaz
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Actualmente: {isDark ? 'Oscuro' : 'Claro'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={isDark ? <LightMode /> : <DarkMode />}
          onClick={toggle}
        >
          Cambiar a {isDark ? 'claro' : 'oscuro'}
        </Button>
      </Stack>
    </Paper>
  );
}
